/**
 * Renders a cross-project activity feed (notes / photos / punch / status).
 * Entries stay in reverse-chronological order but are grouped under the project
 * they belong to (consecutive same-project entries share one header), so it's
 * obvious at a glance which project — and which task — each update is about.
 * Shared by the Home "Recent activity" card and the full /activity page.
 */
import { Link } from 'react-router-dom';
import { Activity, AlertTriangle, Image as ImageIcon, MessageSquare } from 'lucide-react';
import type { ActivityFeedItem } from '@vari-crew/schemas';
import { MediaGallery } from '@/components/MediaGallery';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/cn';

interface ProjectGroup {
  project_id: string;
  project_title: string;
  items: ActivityFeedItem[];
}

/** Group consecutive entries by project, preserving the incoming time order. */
function groupByProject(items: ActivityFeedItem[]): ProjectGroup[] {
  const groups: ProjectGroup[] = [];
  for (const it of items) {
    const last = groups[groups.length - 1];
    if (last && last.project_id === it.project_id) {
      last.items.push(it);
    } else {
      groups.push({
        project_id: it.project_id,
        project_title: it.project_title,
        items: [it],
      });
    }
  }
  return groups;
}

export function ActivityFeedList({ items }: { items: ActivityFeedItem[] }) {
  const groups = groupByProject(items);
  return (
    <div className="space-y-5">
      {groups.map((group, gi) => (
        <div key={`${group.project_id}-${gi}`}>
          {/* Project header — the boundary for everything beneath it. */}
          <Link
            to={`/projects/${group.project_id}`}
            className="inline-flex items-center text-sm font-semibold text-ink hover:text-accent hover:underline"
          >
            {group.project_title}
          </Link>
          <ul className="mt-2 space-y-3 border-l-2 border-line pl-3">
            {group.items.map((it) => (
              <li key={`${it.kind}-${it.id}`} className="flex gap-3">
                <div className="mt-0.5 shrink-0">
                  {it.kind === 'note' && <MessageSquare className="w-4 h-4 text-ink-faint" />}
                  {it.kind === 'punch' && (
                    <AlertTriangle
                      className={cn(
                        'w-4 h-4',
                        it.punch_status === 'closed'
                          ? 'text-ink-faint'
                          : 'text-status-blocked-fg',
                      )}
                    />
                  )}
                  {it.kind === 'media' && <ImageIcon className="w-4 h-4 text-ink-faint" />}
                  {it.kind === 'status' && (
                    <Activity
                      className={cn(
                        'w-4 h-4',
                        it.title?.includes('Blocked') || it.title === 'Paused'
                          ? 'text-status-blocked-fg'
                          : it.title === 'Completed' || it.title?.includes('approved')
                            ? 'text-status-complete-fg'
                            : 'text-ink-faint',
                      )}
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  {/* when · who · task — the project is the group header above. */}
                  <div className="text-xs text-ink-faint flex flex-wrap items-center gap-x-1.5">
                    <span className="whitespace-nowrap">{formatDateTime(it.at)}</span>
                    {it.author_name && <span className="truncate">· {it.author_name}</span>}
                    {it.task_id && (
                      <>
                        <span>·</span>
                        <Link
                          to={`/tasks/${it.task_id}`}
                          className="text-ink-dim hover:text-accent hover:underline truncate"
                        >
                          {it.task_title ?? 'task'}
                        </Link>
                      </>
                    )}
                  </div>

                  {it.kind === 'status' && it.title && (
                    <div className="text-sm font-medium">{it.title}</div>
                  )}
                  {it.kind === 'punch' && it.title && (
                    <div
                      className={cn(
                        'text-sm font-medium',
                        it.punch_status === 'closed' && 'line-through text-ink-faint',
                      )}
                    >
                      {it.title}
                      {it.blocks_recap && it.punch_status !== 'closed' && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-status-blocked-fg font-semibold">
                          Blocks closeout
                        </span>
                      )}
                    </div>
                  )}
                  {it.body && <div className="text-sm whitespace-pre-wrap">{it.body}</div>}
                  {it.kind === 'media' && !it.body && (
                    <div className="text-sm text-ink-dim">
                      {it.media[0]?.kind === 'video' ? 'Video' : 'Photo'} added
                    </div>
                  )}
                  <MediaGallery media={it.media} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
